// Global Instructions Rule Applied!
//
// Agent Oversight — the call log sections (admin only), under the proposal
// Activity on AgentAccessAdminPanel. public.agent_call_log holds one row per
// MCP tool call the gateway handled, refusals included; these sections are
// the "what are agents asking for, and what do they not get?" review the
// owner wanted for deciding which tools / RPCs the MCP server should gain.
//
//   Tool health              per tool: calls, outcome mix, p50 / p95 time.
//   Failed calls  | Searches with no results
//   Arguments agents use | Tools not granted
//   Call log                 every call, filtered on the server, infinite
//                            scroll; a row expands to its arguments + error.
//
// Driven by the page's period (UTC days). A tool name in the insight tables
// is a recordLink-style button that filters the Call log to that tool.
// Chrome: FormSections with antd Tables inside; the Call log copies the
// Activity section's filter row and infinite-scroll idiom.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input, Progress, Select, Table, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import {
  AGENT_CALL_OUTCOMES,
  AgentArgUsage,
  AgentCallInsights,
  AgentCallLogQuery,
  AgentCallLogRow,
  AgentCallOutcome,
  AgentEmptySearch,
  AgentScopeRefusal,
  AgentToolHealth,
  AgentTopFailure
} from '../../../state_manager/data_models/agent_access_admin_model.ts'
import { fetchAgentCallInsights, fetchAgentCallLogAdmin } from '../services/agentAccessAdminService.ts'
import { Period, periodTimestamps, windowLabel } from '../utils/agentOversightPeriod.ts'

const { Text } = Typography

const CALLS_PAGE_SIZE = 100
const SCROLL_THRESHOLD_PX = 300
const CALLS_BODY_HEIGHT = 'calc(100vh - 320px)'
const SEARCH_DEBOUNCE_MS = 300

// Outcome vocabulary (docs/FRONTEND_DESIGN_PATTERNS.md Tag colours): success
// green, nothing-found / reference grey, needs-attention gold (the agent can
// fix the call), failure red. A refusal is policy working, not a fault — gold.
const OUTCOME_TAG: Record<AgentCallOutcome, { label: string; color?: string }> = {
  ok: { label: 'OK', color: 'green' },
  empty: { label: 'Empty' },
  not_found: { label: 'Not found', color: 'gold' },
  invalid_args: { label: 'Invalid args', color: 'gold' },
  refused_scope: { label: 'Not granted', color: 'gold' },
  refused_cap: { label: 'Cap reached', color: 'gold' },
  refused_grant: { label: 'No grant', color: 'gold' },
  error: { label: 'Error', color: 'red' }
}

export const outcomeTag = (outcome: AgentCallOutcome): React.ReactElement => {
  const tag = OUTCOME_TAG[outcome]
  return <Tag color={tag.color}>{tag.label}</Tag>
}

const compactNumber = (v?: number | null): string => Number(v ?? 0).toLocaleString()
const formatDateTime = (value?: string | null): string => (value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '—')
const pct = (part: number, whole: number): string => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—')

// One line of JSON for a table cell; the expanded row shows it pretty-printed.
const argsInline = (args: Record<string, unknown>): string => {
  const text = JSON.stringify(args)
  return text === '{}' ? '—' : text
}

// A count that needs attention as a Tag, a quiet one as plain text.
const countCell = (n: number, color: string): React.ReactNode =>
  n > 0 ? <Tag color={color}>{compactNumber(n)}</Tag> : <Text type='secondary'>0</Text>

interface Props {
  period: Period
  grantOptions: { value: string; label: string }[]
  /** Bumped by the page's Refresh so both reads re-run. */
  refreshKey: number
}

// Read at call time (never captured): the session token refreshes.
const token = (): string | null => getAccessToken(store.getState().auth)

export default function AgentCallLogSections({ period, grantOptions, refreshKey }: Props): React.ReactElement {
  // ── insights ─────────────────────────────────────────────────────────────
  const [insights, setInsights] = useState<AgentCallInsights | null>(null)
  const [insightsLoading, setInsightsLoading] = useState(true)

  const loadInsights = useCallback(async () => {
    setInsightsLoading(true)
    try {
      setInsights(await fetchAgentCallInsights(token(), period))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load agent call insights')
    } finally {
      setInsightsLoading(false)
    }
  }, [period])

  // ── call log ─────────────────────────────────────────────────────────────
  const [grantFilter, setGrantFilter] = useState<string | null>(null)
  const [toolFilter, setToolFilter] = useState<string | null>(null)
  const [kindFilter, setKindFilter] = useState<'read' | 'write' | 'resource' | null>(null)
  const [outcomeFilter, setOutcomeFilter] = useState<AgentCallOutcome[]>([])
  const [searchText, setSearchText] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchText.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchText])

  const [calls, setCalls] = useState<AgentCallLogRow[]>([])
  const [callsCount, setCallsCount] = useState(0)
  const [callsHasMore, setCallsHasMore] = useState(false)
  const [callsLoading, setCallsLoading] = useState(true)
  const [callsLoadingMore, setCallsLoadingMore] = useState(false)
  const callsTableRef = useRef<HTMLDivElement | null>(null)
  const callsSectionRef = useRef<HTMLDivElement | null>(null)

  const callsQuery = useCallback(
    (offset: number): AgentCallLogQuery => ({
      grant_id: grantFilter ?? undefined,
      tool: toolFilter ?? undefined,
      kind: kindFilter ?? undefined,
      outcomes: outcomeFilter.length ? outcomeFilter : undefined,
      search: search || undefined,
      ...periodTimestamps(period),
      limit: CALLS_PAGE_SIZE,
      offset
    }),
    [grantFilter, toolFilter, kindFilter, outcomeFilter, search, period]
  )

  const loadCalls = useCallback(async () => {
    setCallsLoading(true)
    try {
      const page = await fetchAgentCallLogAdmin(token(), callsQuery(0))
      setCalls(page.data)
      setCallsHasMore(page.has_more)
      setCallsCount(page.count)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load the agent call log')
      setCalls([])
      setCallsHasMore(false)
    } finally {
      setCallsLoading(false)
    }
  }, [callsQuery])

  const loadMoreCalls = useCallback(async () => {
    if (callsLoading || callsLoadingMore || !callsHasMore) return
    setCallsLoadingMore(true)
    try {
      const page = await fetchAgentCallLogAdmin(token(), callsQuery(calls.length))
      setCalls((prev) => [...prev, ...page.data])
      setCallsHasMore(page.has_more)
      setCallsCount(page.count)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load more of the agent call log')
      setCallsHasMore(false)
    } finally {
      setCallsLoadingMore(false)
    }
  }, [callsQuery, calls.length, callsLoading, callsLoadingMore, callsHasMore])

  useEffect(() => {
    const body = callsTableRef.current?.querySelector('.ant-table-body') as HTMLElement | null
    if (!body) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) loadMoreCalls()
    }
    body.addEventListener('scroll', onScroll)
    return () => body.removeEventListener('scroll', onScroll)
  }, [loadMoreCalls])

  useEffect(() => {
    loadInsights()
  }, [loadInsights, refreshKey])
  useEffect(() => {
    loadCalls()
  }, [loadCalls, refreshKey])

  // A tool name in an insight table narrows the Call log to it (and to the
  // outcomes that table is about), then brings the log into view.
  const showInLog = (tool: string, outcomes: AgentCallOutcome[] = []) => {
    setToolFilter(tool)
    setOutcomeFilter(outcomes)
    callsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const toolLink = (tool: string, outcomes: AgentCallOutcome[] = []): React.ReactElement => (
    <button
      type='button'
      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
      onClick={() => showInLog(tool, outcomes)}
    >
      {tool}
    </button>
  )

  const toolOptions = useMemo(() => {
    const tools = new Set<string>()
    insights?.tool_health.forEach((t) => tools.add(t.prog__tool_key))
    if (toolFilter) tools.add(toolFilter)
    return Array.from(tools)
      .sort()
      .map((t) => ({ value: t, label: t }))
  }, [insights, toolFilter])

  // ── columns ──────────────────────────────────────────────────────────────
  const healthColumns = [
    {
      title: 'Tool',
      key: 'tool',
      width: 200,
      render: (_: unknown, row: AgentToolHealth) => (
        <span className='whitespace-nowrap'>
          {toolLink(row.prog__tool_key)}
          {row.prog__tool_kind !== 'read' ? (
            <Tag color='purple' style={{ marginLeft: 6 }}>
              {row.prog__tool_kind}
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: 'Calls',
      dataIndex: 'prog__calls',
      key: 'calls',
      width: 80,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'OK',
      key: 'ok',
      width: 150,
      render: (_: unknown, row: AgentToolHealth) => (
        <span className='flex items-center gap-2'>
          <Progress
            size='small'
            percent={row.prog__calls ? Math.round((row.prog__ok / row.prog__calls) * 100) : 0}
            showInfo={false}
            status='normal'
            style={{ width: 80, margin: 0 }}
          />
          <Text style={{ fontSize: 12 }}>{pct(row.prog__ok, row.prog__calls)}</Text>
        </span>
      )
    },
    {
      title: 'Empty',
      key: 'empty',
      width: 80,
      render: (_: unknown, row: AgentToolHealth) => countCell(row.prog__empty, 'default')
    },
    {
      title: 'Not found',
      key: 'not_found',
      width: 90,
      render: (_: unknown, row: AgentToolHealth) => countCell(row.prog__not_found, 'gold')
    },
    {
      title: 'Invalid args',
      key: 'invalid',
      width: 100,
      render: (_: unknown, row: AgentToolHealth) => countCell(row.prog__invalid_args, 'gold')
    },
    {
      title: 'Refused',
      key: 'refused',
      width: 90,
      render: (_: unknown, row: AgentToolHealth) => countCell(row.prog__refused, 'gold')
    },
    {
      title: 'Errors',
      key: 'errors',
      width: 80,
      render: (_: unknown, row: AgentToolHealth) => countCell(row.prog__error, 'red')
    },
    {
      title: 'Median / p95',
      key: 'timing',
      width: 130,
      render: (_: unknown, row: AgentToolHealth) =>
        row.prog__p50_ms === null ? (
          '—'
        ) : (
          <Text type='secondary' style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {compactNumber(row.prog__p50_ms)} / {compactNumber(row.prog__p95_ms)} ms
          </Text>
        )
    }
  ]

  const failureColumns = [
    {
      title: 'Tool',
      key: 'tool',
      width: 170,
      render: (_: unknown, row: AgentTopFailure) => toolLink(row.prog__tool_key, [row.prog__outcome])
    },
    {
      title: 'Outcome',
      key: 'outcome',
      width: 150,
      render: (_: unknown, row: AgentTopFailure) => (
        <>
          {outcomeTag(row.prog__outcome)}
          {row.prog__error_code && row.prog__error_code !== row.prog__outcome ? (
            <Text type='secondary' style={{ fontSize: 12 }}>
              {row.prog__error_code}
            </Text>
          ) : null}
        </>
      )
    },
    {
      title: 'Calls',
      dataIndex: 'prog__calls',
      key: 'calls',
      width: 70,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'Latest example',
      key: 'example',
      render: (_: unknown, row: AgentTopFailure) => (
        <Tooltip title={row.prog__example_error || undefined}>
          <Text code ellipsis style={{ fontSize: 12, maxWidth: 220 }}>
            {argsInline(row.prog__example_args)}
          </Text>
        </Tooltip>
      )
    },
    { title: 'Last', dataIndex: 'prog__last_at', key: 'last', width: 140, render: formatDateTime }
  ]

  const emptySearchColumns = [
    { title: 'Query', dataIndex: 'prog__query', key: 'query', render: (v: string) => <Text>{v}</Text> },
    { title: 'Looking for', dataIndex: 'prog__entity_type', key: 'entity', width: 110 },
    {
      title: 'Times',
      dataIndex: 'prog__calls',
      key: 'calls',
      width: 70,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    { title: 'Last', dataIndex: 'prog__last_at', key: 'last', width: 140, render: formatDateTime }
  ]

  const argColumns = [
    {
      title: 'Tool',
      key: 'tool',
      width: 170,
      render: (_: unknown, row: AgentArgUsage) => toolLink(row.prog__tool_key)
    },
    { title: 'Argument', dataIndex: 'prog__arg_key', key: 'arg', width: 150 },
    {
      title: 'Used in',
      key: 'used',
      width: 150,
      render: (_: unknown, row: AgentArgUsage) => (
        <span className='flex items-center gap-2'>
          <Progress
            size='small'
            percent={row.prog__tool_calls ? Math.round((row.prog__supplied / row.prog__tool_calls) * 100) : 0}
            showInfo={false}
            status='normal'
            style={{ width: 70, margin: 0 }}
          />
          <Text style={{ fontSize: 12 }}>{pct(row.prog__supplied, row.prog__tool_calls)}</Text>
        </span>
      )
    },
    {
      title: 'Top values',
      key: 'values',
      render: (_: unknown, row: AgentArgUsage) =>
        row.prog__top_values.length ? (
          <>
            {row.prog__top_values.map((v) => (
              <Tag key={v.value} style={{ marginBottom: 2 }}>
                {v.value} ×{v.count}
              </Tag>
            ))}
          </>
        ) : (
          <Text type='secondary'>—</Text>
        )
    }
  ]

  const refusalColumns = [
    {
      title: 'Tool',
      key: 'tool',
      render: (_: unknown, row: AgentScopeRefusal) => toolLink(row.prog__tool_key, ['refused_scope'])
    },
    {
      title: 'Attempts',
      dataIndex: 'prog__calls',
      key: 'calls',
      width: 90,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    {
      title: 'Users',
      dataIndex: 'prog__users',
      key: 'users',
      width: 70,
      align: 'right' as const,
      render: (v: number) => compactNumber(v)
    },
    { title: 'Last', dataIndex: 'prog__last_at', key: 'last', width: 140, render: formatDateTime }
  ]

  const callColumns = [
    { title: 'When', dataIndex: 'agent_call_log__called_at', key: 'when', width: 140, render: formatDateTime },
    {
      title: 'Connection',
      key: 'connection',
      width: 200,
      render: (_: unknown, row: AgentCallLogRow) => (
        <>
          <Text>{row.prog__user_name || 'Unknown user'}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.prog__agent_name}
          </Text>
        </>
      )
    },
    {
      title: 'Tool',
      key: 'tool',
      width: 190,
      render: (_: unknown, row: AgentCallLogRow) => (
        <span className='whitespace-nowrap'>
          <Text>{row.agent_call_log__tool_key}</Text>
          {row.agent_call_log__tool_kind !== 'read' ? (
            <Tag color='purple' style={{ marginLeft: 6 }}>
              {row.agent_call_log__tool_kind}
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: 'Outcome',
      key: 'outcome',
      width: 120,
      render: (_: unknown, row: AgentCallLogRow) => outcomeTag(row.agent_call_log__outcome)
    },
    {
      title: 'Arguments',
      key: 'args',
      render: (_: unknown, row: AgentCallLogRow) => (
        <Text code ellipsis style={{ fontSize: 12, maxWidth: 360 }}>
          {argsInline(row.agent_call_log__args)}
        </Text>
      )
    },
    {
      title: 'Rows',
      key: 'rows',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, row: AgentCallLogRow) =>
        row.agent_call_log__row_count === null ? (
          '—'
        ) : (
          <>
            {compactNumber(row.agent_call_log__row_count)}
            {row.agent_call_log__truncated || row.agent_call_log__has_more ? '+' : ''}
          </>
        )
    },
    {
      title: 'Time',
      dataIndex: 'agent_call_log__duration_ms',
      key: 'duration',
      width: 90,
      align: 'right' as const,
      render: (v: number | null) => (v === null ? '—' : `${compactNumber(v)} ms`)
    }
  ]

  const loadingInsights = insightsLoading && !insights

  return (
    <>
      <FormSection
        title='Tool health'
        titleColor='purple'
        scrollable={false}
        loading={loadingInsights}
        headerActions={
          <Text type='secondary' style={{ fontSize: 12 }}>
            Every agent tool call in the period, by outcome — click a tool to see its calls
          </Text>
        }
      >
        <Table
          size='small'
          rowKey='prog__tool_key'
          pagination={false}
          dataSource={insights?.tool_health ?? []}
          columns={healthColumns}
          scroll={{ x: 1000, y: 420 }}
          locale={{ emptyText: 'No agent tool calls in this period' }}
        />
      </FormSection>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <FormSection title='Failed calls' titleColor='red' scrollable={false} loading={loadingInsights}>
          <Table
            size='small'
            rowKey={(row: AgentTopFailure) => `${row.prog__tool_key}|${row.prog__outcome}|${row.prog__error_code ?? ''}`}
            pagination={false}
            dataSource={insights?.top_failures ?? []}
            columns={failureColumns}
            scroll={{ x: 760, y: 360 }}
            locale={{ emptyText: 'No failed calls in this period' }}
          />
        </FormSection>
        <FormSection title='Searches with no results' titleColor='orange' scrollable={false} loading={loadingInsights}>
          <Table
            size='small'
            rowKey={(row: AgentEmptySearch) => `${row.prog__entity_type}|${row.prog__query}`}
            pagination={false}
            dataSource={insights?.empty_searches ?? []}
            columns={emptySearchColumns}
            scroll={{ y: 360 }}
            locale={{ emptyText: 'Every search found something in this period' }}
          />
        </FormSection>
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <FormSection
          title='Arguments agents use'
          titleColor='blue'
          scrollable={false}
          loading={loadingInsights}
          headerActions={
            <Text type='secondary' style={{ fontSize: 12 }}>
              Options left at their default are not counted
            </Text>
          }
        >
          <Table
            size='small'
            rowKey={(row: AgentArgUsage) => `${row.prog__tool_key}|${row.prog__arg_key}`}
            pagination={false}
            dataSource={insights?.arg_usage ?? []}
            columns={argColumns}
            scroll={{ x: 700, y: 360 }}
            locale={{ emptyText: 'No arguments recorded in this period' }}
          />
        </FormSection>
        <FormSection
          title='Tools not granted'
          titleColor='orange'
          scrollable={false}
          loading={loadingInsights}
          headerActions={
            <Text type='secondary' style={{ fontSize: 12 }}>
              Tools agents tried that the user had not authorised
            </Text>
          }
        >
          <Table
            size='small'
            rowKey='prog__tool_key'
            pagination={false}
            dataSource={insights?.scope_refusals ?? []}
            columns={refusalColumns}
            scroll={{ y: 360 }}
            locale={{ emptyText: 'No refused tools in this period' }}
          />
        </FormSection>
      </div>

      <div ref={callsSectionRef}>
        <FormSection
          title='Call log'
          titleColor='gray'
          scrollable={false}
          loading={callsLoading && calls.length === 0}
          headerActions={
            <Text type='secondary' style={{ fontSize: 12 }}>
              Every agent tool call in the period, newest first — refusals included
            </Text>
          }
        >
          {/* Filters in a body row (the Activity idiom); all go to the server. */}
          <div className='flex items-center gap-2 flex-wrap mb-2'>
            <Select
              size='small'
              className='w-64'
              allowClear
              showSearch
              optionFilterProp='label'
              placeholder='All connections'
              value={grantFilter}
              onChange={(value) => setGrantFilter(value ?? null)}
              options={grantOptions}
            />
            <Select
              size='small'
              className='w-48'
              allowClear
              showSearch
              placeholder='All tools'
              value={toolFilter}
              onChange={(value) => setToolFilter(value ?? null)}
              options={toolOptions}
            />
            <Select
              size='small'
              className='w-32'
              allowClear
              placeholder='All kinds'
              value={kindFilter}
              onChange={(value) => setKindFilter(value ?? null)}
              options={[
                { value: 'read', label: 'Reads' },
                { value: 'write', label: 'Writes' },
                { value: 'resource', label: 'Guides' }
              ]}
            />
            <Select
              size='small'
              mode='multiple'
              className='min-w-[220px]'
              allowClear
              placeholder='All outcomes'
              value={outcomeFilter}
              onChange={(value) => setOutcomeFilter(value)}
              options={AGENT_CALL_OUTCOMES.map((o) => ({ value: o, label: OUTCOME_TAG[o].label }))}
              maxTagCount='responsive'
            />
            <Input
              size='small'
              className='w-56'
              allowClear
              placeholder='Arguments or error contain…'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div ref={callsTableRef}>
            <Table
              size='small'
              rowKey='agent_call_log__id'
              pagination={false}
              dataSource={calls}
              columns={callColumns}
              scroll={{ x: 1150, y: CALLS_BODY_HEIGHT }}
              expandable={{
                expandedRowRender: (row: AgentCallLogRow) => (
                  <div className='space-y-2'>
                    {row.agent_call_log__error ? (
                      <Text type='danger' style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                        {row.agent_call_log__error}
                      </Text>
                    ) : null}
                    <pre className='text-xs bg-gray-50 border border-gray-200 rounded p-2 m-0 whitespace-pre-wrap'>
                      {JSON.stringify(row.agent_call_log__args, null, 2)}
                    </pre>
                    {row.agent_call_log__args_truncated ? (
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        Arguments were over 4 KB and are shown truncated.
                      </Text>
                    ) : null}
                  </div>
                ),
                rowExpandable: () => true
              }}
              locale={{ emptyText: callsLoading ? ' ' : 'No agent tool calls match these filters' }}
            />
          </div>
          <div className='h-8 flex items-center text-xs text-gray-400'>
            {callsLoadingMore ? 'Loading more…' : windowLabel(calls.length, callsCount, 'call')}
          </div>
        </FormSection>
      </div>
    </>
  )
}
